import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Divider,
} from "@heroui/react";
import { useTranslations } from "../../../../../i18n";
import { CheckCircle, XCircle, AlertCircle, Clock, Mail } from "lucide-react";

interface ConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function CancelDialog({
  isOpen,
  onClose,
  onConfirm,
}: ConfirmDialogProps) {
  const t = useTranslations("modal-cancellation");

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="text-xl font-bold">{t("title")}</ModalHeader>
        <ModalBody className="gap-4">
          <p className="text-gray-600">{t("intro")}</p>

          {/* Free Cancellation Section */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-semibold text-green-800 flex items-center gap-2 mb-2">
              <CheckCircle size={20} />
              {t("freeCancellation.title")}
            </h3>
            <ul className="space-y-1 text-sm text-green-700">
              <li>• {t("freeCancellation.point1")}</li>
              <li>• {t("freeCancellation.point2")}</li>
              <li>• {t("freeCancellation.point3")}</li>
            </ul>
          </div>

          {/* Late Cancellation Section */}
          <div className="bg-red-50 p-4 rounded-lg">
            <h3 className="font-semibold text-red-800 flex items-center gap-2 mb-2">
              <XCircle size={20} />
              {t("lateCancellation.title")}
            </h3>
            <ul className="space-y-1 text-sm text-red-700">
              <li>• {t("lateCancellation.point1")}</li>
              <li>• {t("lateCancellation.point2")}</li>
              <li>• {t("lateCancellation.point3")}</li>
            </ul>
          </div>

          {/* Workshops Section */}
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="font-semibold text-purple-800 flex items-center gap-2 mb-2">
              <AlertCircle size={20} />
              {t("workshops.title")}
            </h3>
            <ul className="space-y-1 text-sm text-purple-700">
              <li>• {t("workshops.point1")}</li>
              <li>• {t("workshops.point2")}</li>
              <li>• {t("workshops.point3")}</li>
            </ul>
          </div>

          <Divider />

          {/* How to Cancel */}
          <div>
            <h3 className="font-semibold flex items-center gap-2 mb-2">
              <Clock size={18} />
              {t("howToCancel.title")}
            </h3>
            <ol className="space-y-1 text-sm text-gray-600 list-decimal list-inside">
              <li>{t("howToCancel.step1")}</li>
              <li>{t("howToCancel.step2")}</li>
              <li>{t("howToCancel.step3")}</li>
            </ol>
          </div>

          {/* No Shows */}
          <div>
            <h3 className="font-semibold mb-2">{t("noShows.title")}</h3>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• {t("noShows.point1")}</li>
              <li>• {t("noShows.point2")}</li>
              <li>• {t("noShows.point3")}</li>
            </ul>
          </div>

          {/* Exceptions */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
              <Mail size={18} />
              {t("exceptions.title")}
            </h3>
            <p className="text-sm text-blue-700">{t("exceptions.text")}</p>
          </div>

          <Divider />

          <p className="text-xs text-gray-500 italic">{t("agreement")}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onPress={onConfirm}>
            {t("understood")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
